import type {PerkIntegrationConfigComponentProps} from '@cohort/merchants/apps';
import {useTypeformForms} from '@cohort/merchants/apps/typeform/api';
import ConnectionPicker from '@cohort/merchants/components/connections/ConnectionPicker';
import ErrorState from '@cohort/merchants/components/ErrorState';
import SelectInput from '@cohort/merchants/components/form/v2/SelectInput';
import type {SelectOption} from '@cohort/merchants/components/form/v2/SelectPicker';
import Loader from '@cohort/merchants/components/Loader';
import {useConnections} from '@cohort/merchants/hooks/api/Connections';
import {useCurrentMerchant} from '@cohort/merchants/hooks/contexts/currentMerchant';
import type {PerkFormValues} from '@cohort/merchants/pages/perks/perk/edit/utils';
import {Fragment, useEffect, useState} from 'react';
import type {Control, UseFormRegister} from 'react-hook-form';
import {useController} from 'react-hook-form';
import {useTranslation} from 'react-i18next';
import {match} from 'ts-pattern';

type ConfigFormSelectProps = {
  register: UseFormRegister<PerkFormValues>;
  control: Control<PerkFormValues>;
  connectionId: string | null;
};

const ConfigFormSelect: React.FC<ConfigFormSelectProps> = ({register, control, connectionId}) => {
  const merchant = useCurrentMerchant();
  const {t} = useTranslation('app-typeform', {keyPrefix: 'perkIntegrations.form.configComponent'});

  const {
    data: typeformFormsRes,
    isLoading: isTypeformFormsLoading,
    isError: isTypeformFormsError,
    isFetched: isTypeformFormsFetched,
  } = useTypeformForms(merchant.id, connectionId ?? '', {
    enabled: connectionId !== null,
  });

  const typeformForms = typeformFormsRes?.output.forms ?? [];
  const sortedTypeformForms = typeformForms.sort(formA => (formA.settings.is_public ? -1 : 1));

  const formsSelectionContent = match({
    connectionId,
    typeformForms,
    isTypeformFormsLoading,
    isTypeformFormsError,
    isTypeformFormsFetched,
  })
    .with({connectionId: null}, () => <Fragment />)
    .with({isTypeformFormsLoading: true}, () => <Loader size={30} color="gray" />)
    .with({isTypeformFormsError: true}, () => <ErrorState />)
    .with({typeformForms: []}, () => (
      <div className="flex flex-col space-y-2">
        <p>{t('noFormsMessage')}</p>
      </div>
    ))
    .with({isTypeformFormsFetched: true}, () => (
      <SelectInput
        labelPosition="top"
        label={t('labelFormSelect')}
        description={t('descriptionFormSelect')}
        displayMode="expand"
        options={sortedTypeformForms.map(
          form =>
            ({
              value: form.id,
              label: form.settings.is_public
                ? form.title
                : t('privateFormLabel', {title: form.title}),
              isDisabled: !form.settings.is_public,
            }) satisfies SelectOption
        )}
        name="integration.config.formId"
        register={register}
        control={control}
      />
    ))
    .otherwise(() => <ErrorState />);

  return formsSelectionContent;
};

const TypeformFormPerkIntegrationConfigComponent: React.FC<PerkIntegrationConfigComponentProps> = ({
  formReturn: {register, control},
}) => {
  const {field: connectionIdField} = useController({control, name: 'integration.connectionId'});
  const [showFormSelect, setShowFormSelect] = useState(false);
  const [connectionId, setConnectionId] = useState<string | null>(connectionIdField.value);
  const merchant = useCurrentMerchant();
  const {data: connections} = useConnections(merchant.id, {
    connectorId: ['typeform'],
  });

  useEffect(() => {
    if (connections === undefined) {
      return;
    }
    const readyConnections = connections.filter(connection => connection.status === 'ready');
    if (readyConnections.length > 0) {
      setConnectionId(readyConnections[0]?.id ?? null);
      setShowFormSelect(true);
    }
  }, [connections, connectionIdField.value]);

  const handleConnectionModalClose = (): void => {
    // @Devs, show the Forms select only when the modal is closed and the connection is ready.
    setShowFormSelect(true);
  };

  return (
    <div className="space-y-4">
      <ConnectionPicker
        appId="typeform"
        register={register}
        control={control}
        onConnectionModalClose={handleConnectionModalClose}
      />
      {showFormSelect && (
        <ConfigFormSelect
          register={register}
          control={control}
          connectionId={connectionIdField.value ?? connectionId}
        />
      )}
    </div>
  );
};

export default TypeformFormPerkIntegrationConfigComponent;
